import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ApiConnectionString } from "../../constants/ApiConntection";
import { useParams, useLocation } from "react-router-dom";

const MEMBOTIC_BASE_URL = "https://membotic.com";

function readParams(search) {
  const p = new URLSearchParams(search ?? window.location.search);
  const get = (k, d) => p.get(k) ?? d;
  const num = (k, d) => {
    const v = parseInt(p.get(k), 10);
    return isNaN(v) ? d : v;
  };

  const primary = get("primary", "#2d6ef7");
  const btnTextColour = get("btnTextColour", "#ffffff");
  const btnHover = get("btnHover", primary);
  const btnTextHover = get("btnTextHover", btnTextColour);
  const priceColour = get("priceColour", "#ffffff");

  return {
    primary,
    cardBg: get("cardBg", "#ffffff"),
    priceBadge: get("priceBadge", "#212529"),
    priceColour,
    bgColour: get("bgColour", "#f8f9fa"),
    font: get("font", "Arial"),
    baseSize: num("baseSize", 16),
    headingWeight: num("headingWeight", 600),
    radius: num("radius", 12),
    shadow: get("shadow", "sm"),
    btnShape: get("btnShape", "rounded"),
    btnSize: get("btnSize", "md"),
    headerAlign: get("headerAlign", "center"),
    btnTextColour,
    btnHover,
    btnTextHover
  };
}

export default function WidgitProducts() {
  const [subscription, setSubscription] = useState([]);
  const [payAsGo, setPayAsGo] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(0);
  const [club, setClub] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const location = useLocation();

  const theme = useMemo(() => readParams(location.search), [location.search]);

  const itemPaddingClass = "p-3";
  const itemShadowClass =
    theme.shadow === "lg"
      ? "shadow-lg"
      : theme.shadow === "md"
      ? "shadow"
      : theme.shadow === "sm"
      ? "shadow-sm"
      : "";
  const btnSizeClass =
    theme.btnSize === "lg" ? "btn-lg" : theme.btnSize === "sm" ? "btn-sm" : "";
  const btnShapeClass =
    theme.btnShape === "pill"
      ? "rounded-pill"
      : theme.btnShape === "square"
      ? "rounded-0"
      : "rounded";
  const headerAlignClass =
    theme.headerAlign === "left"
      ? "text-start"
      : theme.headerAlign === "right"
      ? "text-end"
      : "text-center";

  const backgroundValue =
    theme.bgColour.toLowerCase() === "transparent"
      ? "transparent"
      : theme.bgColour;

  let ConvertToCurrency = (amount, country) => {
    let currency = "GBP";
    let locale = "en-GB";
    switch (country) {
      case "GB":
        currency = "GBP";
        locale = "en-GB";
        break;
      case "US":
        currency = "USD";
        locale = "en-US";
        break;
      case "AU":
        currency = "AUD";
        locale = "en-AU";
        break;
      case "CA":
        currency = "CAD";
        locale = "en-CA";
        break;
    }
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency
    }).format(amount);
  };

  const goTopLevel = (url) => {
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = url;
        return;
      }
    } catch {}
    window.location.href = url;
  };

  useEffect(() => {
    setIsLoading(true);
    const fetchClub = async () => {
      try {
        const clubResponse = await axios.get(
          `${ApiConnectionString}/api/club/${id}`
        );
        if (clubResponse.status === 200) setClub(clubResponse.data);
        const subResponse = await axios.get(
          `${ApiConnectionString}/api/products?ClubId=${id}`
        );
        const filterSub = subResponse.data.filter(
          (product) => product.recurring === true
        );
        setSubscription(filterSub);
        const filterPayasGo = subResponse.data.filter(
          (product) => product.recurring === false
        );
        setPayAsGo(filterPayasGo);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      }
    };
    fetchClub();
  }, [id]);

  if (!club) return null;

  const css = `
.embeddedProductsTheme {
  --mb-btn-bg: ${theme.primary};
  --mb-btn-text: ${theme.btnTextColour};
  --mb-btn-hover: ${theme.btnHover};
  --mb-btn-text-hover: ${theme.btnTextHover};
  --mb-card-bg: ${theme.cardBg};
  --mb-price-badge: ${theme.priceBadge};
  --mb-price-colour: ${theme.priceColour};
  --mb-bg: ${backgroundValue};
  font-size: ${theme.baseSize}px;
  background-color: var(--mb-bg);
}
.embeddedProductsTheme h3,
.embeddedProductsTheme h5 { font-weight: ${theme.headingWeight}; }
.embeddedProductsTheme .list-group-item {
  background-color: var(--mb-card-bg);
  border-radius: ${theme.radius}px;
}
.embeddedProductsTheme .badge {
  background-color: var(--mb-price-badge);
  color: var(--mb-price-colour);
}
.embeddedProductsTheme .btn-primary {
  --bs-btn-color: var(--mb-btn-text);
  --bs-btn-bg: var(--mb-btn-bg);
  --bs-btn-border-color: var(--mb-btn-bg);
  --bs-btn-hover-bg: var(--mb-btn-hover);
  --bs-btn-hover-border-color: var(--mb-btn-hover);
  --bs-btn-hover-color: var(--mb-btn-text-hover);
}
.embeddedProductsTheme .btn-link { color: var(--mb-btn-bg); }
`;

  return (
    <div
      className="embeddedProductsTheme"
      style={{ fontFamily: theme.font }}
    >
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div className="container py-5 d-flex justify-content-center">
        <div className="col-12 col-md-10 col-lg-8">
          {subscription.length > 0 && (
            <>
              <div className={`${headerAlignClass} mb-4`}>
                <p className="h3 fw-bold" style={{ fontFamily: theme.font }}>
                  Subscriptions
                </p>
              </div>
              <div className="list-group mb-5">
                {subscription.map((prod) => (
                  <div
                    key={prod.id}
                    className={`list-group-item ${itemPaddingClass} ${itemShadowClass} mb-3`}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p
                          className="h5 mb-1 fw-semibold"
                          style={{ fontFamily: theme.font }}
                        >
                          {prod.name}
                        </p>
                        <p
                          className="text-muted small"
                          style={{ fontFamily: theme.font }}
                        >
                          {prod.interval.charAt(0).toUpperCase() +
                            prod.interval.slice(1)}{" "}
                          recurring payment
                        </p>
                      </div>
                      <span
                        className="badge fs-6"
                        style={{ fontFamily: theme.font }}
                      >
                        {ConvertToCurrency(prod.price / 100, club.country)}
                      </span>
                    </div>
                    {prod.description && (
                      <div className="text-muted small mt-2">
                        <p style={{ fontFamily: theme.font }}>
                          {prod.description}
                        </p>
                      </div>
                    )}
                    <div className="mt-3">
                      <button
                        className={`btn btn-primary ${btnSizeClass} ${btnShapeClass}`}
                        style={{ fontFamily: theme.font }}
                        onClick={() =>
                          goTopLevel(
                            `${MEMBOTIC_BASE_URL}/embeddedProduct/${id}/${prod.id}`
                          )
                        }
                      >
                        <p className="m-0" style={{ fontFamily: theme.font }}>
                          Subscribe
                        </p>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <hr />
            </>
          )}
          {payAsGo.length > 0 && (
            <>
              <div className={`${headerAlignClass} mb-4`}>
                <p className="h3 fw-bold" style={{ fontFamily: theme.font }}>
                  Flexible Class Passes
                </p>
              </div>
              <div className="list-group">
                {payAsGo.map((prod) => (
                  <div
                    key={prod.id}
                    className={`list-group-item ${itemPaddingClass} ${itemShadowClass} mb-3`}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p
                          className="h5 mb-1 fw-semibold"
                          style={{ fontFamily: theme.font }}
                        >
                          {prod.name}
                        </p>
                        <p
                          className="text-muted small"
                          style={{ fontFamily: theme.font }}
                        >
                          Classes: {prod.token}
                        </p>
                      </div>
                      <span
                        className="badge fs-6"
                        style={{ fontFamily: theme.font }}
                      >
                        {ConvertToCurrency(prod.price / 100, club.country)}
                      </span>
                    </div>
                    {prod.description && (
                      <div className="text-muted small mt-2">
                        <p style={{ fontFamily: theme.font }}>
                          {prod.description}
                        </p>
                      </div>
                    )}
                    <div className="mt-3">
                      <button
                        className={`btn btn-primary ${btnSizeClass} ${btnShapeClass}`}
                        style={{ fontFamily: theme.font }}
                        onClick={() =>
                          goTopLevel(
                            `${MEMBOTIC_BASE_URL}/embeddedProduct/${id}/${prod.id}`
                          )
                        }
                      >
                        <p className="m-0" style={{ fontFamily: theme.font }}>
                          Buy
                        </p>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <hr />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
